/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   CORE / MAIL-TRIGGERS — Single-Source-Katalog der Custom-Mail-Auslöser (V1)
   =============================================================================
   S226 CUT C (Mail-Builder). Eine Wahrheitsquelle für die Trigger-Events, die
   eine eigene Mail-Regel (`custom_mail_rules.trigger_event`) auslösen können.
   Speist DREIFACH — kein Drift zwischen UI, DB und Versand:

     1. Builder-Dropdown: gruppiertes <optgroup> je Kategorie (dieselbe Taxonomie
        wie die Vorlagen-Chips 📅 Reservierungen · 🍔 Bestellungen · 🛵 Lieferungen).
        Der Trigger KASKADIERT: seine Wahl fixiert (a) die verfügbaren Platzhalter-
        Chips (`tokens`), (b) ob das Zeitversatz-Feld erscheint (`timeAnchored`),
        (c) die Empfänger-/Consent-Domäne (`recipientDomain`).
     2. DB-CHECK auf `custom_mail_rules.trigger_event` (die `id`-Liste hier ist
        exakt der CHECK-Katalog der Migration phase_166).
     3. Enqueue-Verdrahtung: `gg_enqueue_custom_mails(event, …)` wird für genau
        diese `id`-Events aufgerufen (Sofort-Trigger tg_orders/tg_reservations,
        zeitverankert via Cron enqueue_custom_mail_schedules).

   BEWUSST OHNE IMPORTS (analog mail-kinds.js): der Katalog muss überall
   importierbar sein, ohne einen Zyklus auszulösen. Reine Daten + reine Helfer.

   PIVOT-Hinweis (2026-07-08): Werbliche Custom-Mails an `recipient_mode='customer'`
   gehen NUR an Double-Opt-in-bestätigte Abonnenten (newsletter_subscribers.
   status='confirmed') — das Consent-Gate sitzt DB-seitig in gg_enqueue_custom_mails,
   nicht hier. Dieser Katalog beschreibt nur, WANN ausgelöst wird.
============================================================================ */

/** Kategorien = Taxonomie der Vorlagen-Chips (Reihenfolge = Dropdown-Reihenfolge). */
export const MAIL_TRIGGER_CATEGORIES = Object.freeze([
  { id: 'reservations', label: 'Reservierungen', icon: '📅' },
  { id: 'orders',       label: 'Bestellungen',   icon: '🍔' },
  { id: 'delivery',     label: 'Lieferungen',    icon: '🛵' },
]);

// Token-Sätze pro Domäne (Platzhalter-Chips im Builder). Der gesetzlich
// vorgeschriebene {{unsubscribe_link}}-Footer wird serverseitig angehängt und ist
// KEIN Body-Token (im Builder als fixer grauer Block sichtbar, nicht editierbar).
const TOKENS_ORDER = Object.freeze(['{{name}}', '{{order_number}}', '{{pickup_date}}', '{{pickup_time}}', '{{total}}', '{{items}}', '{{brand}}']);
const TOKENS_RESERVATION = Object.freeze(['{{name}}', '{{guests}}', '{{reservation_date}}', '{{reservation_time}}', '{{brand}}']);

/**
 * Trigger-Event-Katalog V1 (exakt die bestehenden Lifecycle-Punkte).
 *   id              = custom_mail_rules.trigger_event (DB-CHECK-Wert)
 *   category        = Dropdown-optgroup / Chip-Taxonomie
 *   label           = Klartext im Dropdown + in der Regel-Zeile
 *   tokens          = kontextabhängige Platzhalter-Chips
 *   timeAnchored    = true → Zeitversatz-Feld (X Std./Min. vor/nach) erscheint
 *   recipientDomain = 'order' | 'reservation' → Empfänger-/Payload-/Gate-Kontext
 *   entity          = Trigger-Tabelle (Doku/Verdrahtung)
 */
export const MAIL_TRIGGERS = Object.freeze([
  // ===== Reservierungen =====
  {
    id: 'reservation_confirmed', category: 'reservations', label: 'Reservierung angenommen',
    tokens: TOKENS_RESERVATION, timeAnchored: false, recipientDomain: 'reservation', entity: 'reservations',
  },
  {
    id: 'reservation_declined', category: 'reservations', label: 'Reservierung abgelehnt',
    tokens: TOKENS_RESERVATION, timeAnchored: false, recipientDomain: 'reservation', entity: 'reservations',
  },
  {
    id: 'reservation_cancelled', category: 'reservations', label: 'Reservierung storniert',
    tokens: TOKENS_RESERVATION, timeAnchored: false, recipientDomain: 'reservation', entity: 'reservations',
  },
  {
    id: 'reservation_start', category: 'reservations', label: 'Reservierungstermin (zeitversetzt)',
    tokens: TOKENS_RESERVATION, timeAnchored: true, recipientDomain: 'reservation', entity: 'reservations',
  },
  // ===== Bestellungen =====
  {
    id: 'order_confirmed', category: 'orders', label: 'Bestellung angenommen',
    tokens: TOKENS_ORDER, timeAnchored: false, recipientDomain: 'order', entity: 'orders',
  },
  {
    id: 'order_ready', category: 'orders', label: 'Bestellung abholbereit',
    tokens: TOKENS_ORDER, timeAnchored: false, recipientDomain: 'order', entity: 'orders',
  },
  {
    id: 'order_paid', category: 'orders', label: 'Bestellung bezahlt',
    tokens: TOKENS_ORDER, timeAnchored: false, recipientDomain: 'order', entity: 'orders',
  },
  {
    id: 'order_cancelled', category: 'orders', label: 'Bestellung storniert',
    tokens: TOKENS_ORDER, timeAnchored: false, recipientDomain: 'order', entity: 'orders',
  },
  // ===== Lieferungen =====
  {
    id: 'delivery_out_for_delivery', category: 'delivery', label: 'Lieferung unterwegs',
    tokens: TOKENS_ORDER, timeAnchored: false, recipientDomain: 'order', entity: 'orders',
  },
  {
    id: 'delivery_delivered', category: 'delivery', label: 'Lieferung zugestellt',
    tokens: TOKENS_ORDER, timeAnchored: false, recipientDomain: 'order', entity: 'orders',
  },
]);

/** Alle gültigen Trigger-Event-IDs (= DB-CHECK-Katalog). */
export const MAIL_TRIGGER_IDS = Object.freeze(MAIL_TRIGGERS.map(t => t.id));

/** Katalog-Lookup. Null bei unbekanntem Trigger. */
export function getTrigger(id) {
  return MAIL_TRIGGERS.find(t => t.id === id) || null;
}

/** True, wenn der Trigger einen Zeit-Anker hat (Zeitversatz-Feld einblenden). */
export function isTriggerTimeAnchored(id) {
  const t = getTrigger(id);
  return !!(t && t.timeAnchored);
}

/** Platzhalter-Chips für den Trigger (leeres Array bei unbekanntem Trigger). */
export function tokensForTrigger(id) {
  const t = getTrigger(id);
  return t ? t.tokens.slice() : [];
}

/** Empfänger-/Payload-Domäne des Triggers ('order' | 'reservation' | null). */
export function recipientDomainForTrigger(id) {
  const t = getTrigger(id);
  return t ? t.recipientDomain : null;
}

/**
 * Dropdown-Gruppen fürs Builder-<select> (optgroup je Kategorie, in
 * Katalog-Reihenfolge; leere Kategorien fallen raus).
 * → [{ id, label, icon, options: [{ id, label }] }]
 */
export function triggerGroupsForDropdown() {
  return MAIL_TRIGGER_CATEGORIES
    .map(cat => ({
      id: cat.id, label: cat.label, icon: cat.icon,
      options: MAIL_TRIGGERS.filter(t => t.category === cat.id).map(t => ({ id: t.id, label: t.label })),
    }))
    .filter(g => g.options.length > 0);
}
